// Trends page — player drawer: one small chart per metric for both blocks, the conclusions in
// full, and (admin only) the basis/wissel role override. Full screen below ~900px.
import React, { useEffect, useRef, useState } from 'react';
import { X } from 'lucide-react';
import {
  METRICS, BLOCK_LABEL, StateChip, MetricChart, blockSlots, fmtPct, fmtDate, stateVisual,
  HIGH_VARIATION_METRICS, HIGH_VARIATION_NOTE, NOT_APPLICABLE_LABEL,
} from './trendsShared';

function BlockStatus({ player, block }) {
  if (block === 'match') {
    return <span>Te weinig wedstrijden van 60+ minuten (n = {player.match.n})</span>;
  }
  const t = player.training;
  if (t.weeks.every(w => w.excluded === 'no_training')) {
    return <span>{t.lastGpsDate ? `Geen GPS-data sinds ${fmtDate(t.lastGpsDate)}` : 'Geen GPS-data gevonden'}</span>;
  }
  if (t.state === 'rehab') return <span>Revalidatie in {t.rehabWeeks} van {t.weeks.length} weken</span>;
  return <span>Te weinig geldige weken (n = {t.nValid})</span>;
}

function RoleOverride({ player, onChange, busy, error }) {
  const current = player.roleSource === 'override' ? player.role : 'auto';
  const options = [
    { id: 'auto', label: 'Automatisch' },
    { id: 'basis', label: 'Basis' },
    { id: 'wissel', label: 'Wissel' },
  ];
  return (
    <div style={{ marginTop: 10 }}>
      <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 6 }}>
        Rol (alleen admin) — automatisch: {player.startsLast8} van de laatste 8 competitiewedstrijden basis
      </div>
      <div role="radiogroup" aria-label="Rol" style={{ display: 'inline-flex', border: '1px solid var(--border-strong)', borderRadius: 8, overflow: 'hidden' }}>
        {options.map(o => (
          <button key={o.id} role="radio" aria-checked={current === o.id} disabled={busy} onClick={() => current !== o.id && onChange(o.id)}
            style={{ padding: '5px 12px', border: 'none', fontSize: 12, fontWeight: 600, cursor: busy ? 'default' : 'pointer',
              background: current === o.id ? 'var(--text-primary)' : 'var(--bg-card)', color: current === o.id ? 'var(--bg-card)' : 'var(--text-secondary)' }}>
            {o.label}
          </button>
        ))}
      </div>
      {error && <div style={{ fontSize: 11, color: 'var(--tr-down-ink)', marginTop: 6 }}>{error}</div>}
    </div>
  );
}

export default function TrendsPlayerDrawer({ player, isAdmin, fullScreen, onClose, onRoleChange }) {
  const closeRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    closeRef.current?.focus();
    const onKey = e => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const changeRole = async role => {
    setBusy(true); setError(null);
    try { await onRoleChange(player.playerId, role); }
    catch (e) { setError(String(e.message || e).includes('403') ? 'Alleen admins kunnen de rol wijzigen.' : 'Opslaan mislukt.'); }
    finally { setBusy(false); }
  };

  const blocks = ['match', 'training'];
  return (
    <div role="presentation" onClick={onClose}
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 1000, display: 'flex', justifyContent: 'flex-end' }}>
      <aside role="dialog" aria-modal="true" aria-label={`Trends — ${player.name}`} onClick={e => e.stopPropagation()}
        style={{ width: fullScreen ? '100vw' : 'min(760px, 100vw)', height: '100%', overflowY: 'auto', background: 'var(--bg-app)',
          borderLeft: fullScreen ? 'none' : '1px solid var(--border-strong)', padding: fullScreen ? 16 : 20, boxSizing: 'border-box' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
          <div>
            <h3 style={{ margin: 0, fontSize: '1.1rem', fontWeight: 700, color: 'var(--text-primary)' }}>{player.name}</h3>
            <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 3 }}>
              {player.position} · {player.role === 'basis' ? 'Basis' : 'Wissel'}{player.roleSource === 'override' ? ' (handmatig)' : ''} · {player.startsLast8}/8 basis
            </div>
            {isAdmin && <RoleOverride player={player} onChange={changeRole} busy={busy} error={error} />}
          </div>
          <button ref={closeRef} onClick={onClose} aria-label="Sluiten"
            style={{ border: '1px solid var(--border-strong)', background: 'var(--bg-card)', color: 'var(--text-secondary)', borderRadius: 8, padding: 6, cursor: 'pointer' }}>
            <X size={16} />
          </button>
        </div>

        <section style={{ marginTop: 16, background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 10, padding: 12 }}>
          <div style={{ fontSize: 10.5, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.4, color: 'var(--text-muted)', marginBottom: 6 }}>Conclusies</div>
          <ul style={{ margin: 0, paddingLeft: 18, color: 'var(--text-primary)', fontSize: 13, lineHeight: 1.5 }}>
            {player.conclusions.map((c, i) => <li key={i}>{c.text}</li>)}
          </ul>
        </section>

        <div style={{ display: 'grid', gridTemplateColumns: fullScreen ? '1fr' : '1fr 1fr', gap: 10, marginTop: 14 }}>
          {!fullScreen && blocks.map(b => (
            <div key={b} style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.4 }}>
              {BLOCK_LABEL[b]} <span style={{ fontWeight: 500, textTransform: 'none' }}>{b === 'match' ? '— per 90, 60+ min' : '— t.o.v. squad-mediaan'}</span>
            </div>
          ))}
          {METRICS.map(m => blocks.map(b => {
            const mm = player[b].metrics[m.key];
            const usable = player[b].state === 'ok';
            const v = stateVisual(mm.state);
            return (
              <div key={`${m.key}-${b}`} style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 10, padding: '8px 10px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: 'var(--text-primary)', marginBottom: 2 }}>
                  <strong>{m.label}</strong>
                  {fullScreen && <span style={{ color: 'var(--text-muted)' }}>· {BLOCK_LABEL[b]}</span>}
                  <span style={{ flex: 1 }} />
                  {usable && <StateChip state={mm.state} />}
                  <span style={{ fontVariantNumeric: 'tabular-nums', fontWeight: 600 }}>
                    {usable && mm.state !== 'not_applicable' && mm.state !== 'insufficient' ? fmtPct(mm.changePct) : ''}
                  </span>
                  <span style={{ color: 'var(--text-muted)', fontSize: 11 }}>
                    {mm.state === 'not_applicable' ? 'n.v.t.' : usable ? v.word : ''}
                  </span>
                </div>
                {!usable && <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 2 }}><BlockStatus player={player} block={b} /></div>}
                {mm.state === 'not_applicable' && <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 2 }}>{NOT_APPLICABLE_LABEL}</div>}
                <MetricChart slots={blockSlots(player, b, m.key)} mm={mm} block={b} metricKey={m.key} />
                <div style={{ fontSize: 10.5, color: 'var(--text-muted)' }}>
                  n = {mm.n} · stabiel &lt; {mm.bandP80}% · duidelijk ≥ {mm.bandP95}%
                  {HIGH_VARIATION_METRICS.includes(m.key) && mm.state !== 'not_applicable' ? ` · ${HIGH_VARIATION_NOTE}` : ''}
                </div>
              </div>
            );
          }))}
        </div>
        <p style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 12 }}>
          Grijze zone: stabiel gebied (gemiddelde ± de helft van de stabiel-band) — een fitlijn die erbinnen blijft is stabiel. Stippellijn 1.0 (training): squad-mediaan van die week.
          Gestippelde punten tellen niet mee (reden erboven). In training zijn gevulde punten weken met een basisplaats, open punten weken zonder.
        </p>
      </aside>
    </div>
  );
}
